import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getCurrentMe, resetSessionCatalogs } from "@/api";
import {
  clearPersistedAuthSession,
  isAuthSessionStorageEvent,
  loadPersistedAuthSession,
  persistAuthSession,
  type AuthSession,
  type AuthSessionSeed,
} from "@/auth";
import { clearJWT, isHttpClientError, onSessionInvalidated, setJWT } from "@/http";
import { createLogger } from "@/logging";
import {
  appStateReducer,
  createInitialAppState,
  initializeAppState,
  updateAuthSessionFromProfile,
  type AppState,
  type CurrentUserProfile,
  type InitializeAppStateOptions,
  type VolatileAppData,
} from "./appState";
import { appStateHandler } from "./appStateHandler";
import type { AppSessionEndReason, AppSessionLifecycle } from "./AppSessionLifecycle";

const logger = createLogger("app/state");

type AppStateContextValue = {
  state: AppState;
  startSession: (seed: AuthSessionSeed) => Promise<void>;
  endSession: () => void;
  // 내 프로필을 바꾼 응답을 세션에 반영한다. PATCH /v1/me 는 GET 과 같은 전체 상태를 준다.
  // 그래서 다시 GET 하지 않는다.
  applyCurrentUserProfile: (profile: CurrentUserProfile) => void;
  patchVolatileData: (value: Partial<VolatileAppData>) => void;
  resetVolatileData: () => void;
};

type AppStateProviderProps = InitializeAppStateOptions & {
  children: ReactNode;
  splash: ReactNode;
  sessionLifecycle: AppSessionLifecycle;
};

const AppStateContext = createContext<AppStateContextValue | null>(null);

export function AppStateProvider({
  children,
  splash,
  minimumSplashDurationMs,
  sessionLifecycle,
}: AppStateProviderProps) {
  const [state, dispatch] = useReducer(appStateReducer, undefined, () => createInitialAppState());
  // 스플래시 표시 여부는 UI 상태로 두고 AppState에는 앱 데이터만 둔다.
  const [isInitialized, setIsInitialized] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const disposeSession = useCallback(
    (reason: AppSessionEndReason, clearPersistedSession: boolean): void => {
      resetSessionCatalogs();
      sessionLifecycle.dispose(reason);
      clearJWT();
      if (clearPersistedSession) {
        clearPersistedAuthSession();
      }
      dispatch({ type: "auth/sessionEnded" });
    },
    [sessionLifecycle],
  );

  // 세션 활성화 공통 절차다. 로그인 응답의 seed 에 /v1/me 결과를 추가해야 세션이 완성된다.
  const activateSession = useCallback(
    async (
      seed: AuthSessionSeed,
    ): Promise<{ session: AuthSession; profile: CurrentUserProfile }> => {
      resetSessionCatalogs();
      setJWT(seed.accessToken);
      const profile = await getCurrentMe();
      const hydrated = updateAuthSessionFromProfile(seed, profile);
      persistAuthSession(hydrated);
      sessionLifecycle.start(hydrated);
      return { session: hydrated, profile };
    },
    [sessionLifecycle],
  );

  useEffect(() => {
    let disposed = false;
    const startedAt = new Date().toISOString();

    setIsInitialized(false);
    dispatch({ type: "app/initializeStarted", startedAt });

    void initializeAppState({ minimumSplashDurationMs })
      .then(async (initializedState) => {
        if (disposed) {
          return;
        }

        let nextState = initializedState;
        // 복구된 세션은 API 호출 전에 메모리 JWT를 먼저 채운다.
        if (initializedState.auth.status === "signedIn") {
          setJWT(initializedState.auth.session.accessToken);
          nextState = await refreshCurrentUserProfile(initializedState);
        } else {
          clearJWT();
        }
        if (disposed) {
          return;
        }
        if (nextState.auth.status === "signedIn") {
          sessionLifecycle.start(nextState.auth.session);
        }
        dispatch({ type: "app/initializeSucceeded", state: nextState });
        setIsInitialized(true);
        void appStateHandler.notifyInitialized(nextState);
      })
      .catch((error: unknown) => {
        if (disposed) {
          return;
        }
        logger.error("Failed to initialize app state.", error);
        const errorMessage = "앱을 시작하지 못했습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.";
        clearJWT();
        dispatch({
          type: "app/initializeFailed",
          errorMessage,
        });
        setIsInitialized(true);
      });

    return () => {
      disposed = true;
      sessionLifecycle.dispose("appUnmount");
    };
  }, [minimumSplashDurationMs, sessionLifecycle]);

  // 다른 탭에서 로그인/로그아웃이 발생하면 이 탭에도 동일하게 반영한다.
  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (!isAuthSessionStorageEvent(event)) {
        return;
      }

      const nextSession = loadPersistedAuthSession();
      if (nextSession === null) {
        disposeSession("remoteLogout", false);
        return;
      }

      resetSessionCatalogs();
      setJWT(nextSession.accessToken);
      sessionLifecycle.start(nextSession);
      dispatch({ type: "auth/sessionStarted", session: nextSession });
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [disposeSession, sessionLifecycle]);

  // 백엔드가 토큰을 401로 거부하면 저장된 세션을 비우고 React 상태도 로그아웃 처리한다.
  useEffect(() => {
    return onSessionInvalidated(() => {
      disposeSession("invalidated", true);
    });
  }, [disposeSession]);

  // 브라우저 종료는 비동기 완료를 기다릴 수 없다. 그래서 동기 정리를 best-effort로 실행한다.
  // 서버의 업로드 소유권은 lease 가 만료되면 회수된다.
  // bfcache 에서 복귀하면 저장된 인증 세션으로 리소스만 재시작한다.
  useEffect(() => {
    const handlePageHide = () => sessionLifecycle.dispose("browserExit");
    const handlePageShow = (event: PageTransitionEvent) => {
      const current = stateRef.current;
      if (event.persisted && current.auth.status === "signedIn") {
        sessionLifecycle.start(current.auth.session);
      }
    };
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [sessionLifecycle]);

  const value = useMemo<AppStateContextValue>(
    () => ({
      state,
      startSession: async (seed) => {
        const { session: hydratedSession } = await activateSession(seed);
        dispatch({ type: "auth/sessionStarted", session: hydratedSession });
      },
      endSession: () => {
        disposeSession("logout", true);
      },
      applyCurrentUserProfile: (profile) => {
        const current = stateRef.current;
        if (current.auth.status !== "signedIn") {
          return;
        }
        dispatch({ type: "auth/currentUserLoaded", profile });
        persistAuthSession(updateAuthSessionFromProfile(current.auth.session, profile));
      },
      patchVolatileData: (volatileData) =>
        dispatch({ type: "volatile/patch", value: volatileData }),
      resetVolatileData: () => {
        resetSessionCatalogs();
        dispatch({ type: "volatile/reset" });
      },
    }),
    [activateSession, disposeSession, state],
  );

  return (
    <AppStateContext.Provider value={value}>
      {isInitialized ? children : splash}
    </AppStateContext.Provider>
  );
}

async function refreshCurrentUserProfile(state: AppState): Promise<AppState> {
  if (state.auth.status !== "signedIn") {
    return state;
  }

  try {
    const profile = await getCurrentMe();
    const nextState = appStateReducer(state, { type: "auth/currentUserLoaded", profile });
    if (nextState.auth.status === "signedIn") {
      persistAuthSession(nextState.auth.session);
    }
    return nextState;
  } catch (error) {
    if (isHttpClientError(error) && error.status === 401) {
      clearJWT();
      clearPersistedAuthSession();
      return appStateReducer(state, { type: "auth/sessionEnded" });
    }
    return state;
  }
}

export function useAppState(): AppStateContextValue {
  const context = useContext(AppStateContext);
  if (context === null) {
    throw new Error("useAppState must be used inside <AppStateProvider>.");
  }
  return context;
}
